import React, { createContext, useContext, useState } from 'react';
import {
    Toast,
    ToastProvider as RadixToastProvider,
    ToastViewport,
    ToastAction,
    ToastClose,
    ToastTitle,
    ToastDescription,
} from './toast';

type ToastProps = React.ComponentProps<typeof Toast> & {
    title?: string;
    description?: string;
    action?: React.ReactElement<typeof ToastAction>;
};

type ToastActionElement = React.ReactElement<typeof ToastAction>;

interface ToastContextType {
    toast: (props: ToastProps) => void;
    dismiss: (toastId?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<ToastProps[]>([]);

    const toast = (props: ToastProps) => {
        const id = Math.random().toString(36).substr(2, 9);
        setToasts((prev) => [...prev, { ...props, id }]);
    };

    const dismiss = (toastId?: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
    };

    return (
        <ToastContext.Provider value={{ toast, dismiss }}>
            {children}
            <RadixToastProvider>
                {toasts.map((toast) => (
                    <Toast key={toast.id} {...toast}>
                        <div className="grid gap-1">
                            {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
                            {toast.description && <ToastDescription>{toast.description}</ToastDescription>}
                        </div>
                        {toast.action && <ToastAction>{toast.action}</ToastAction>}
                        <ToastClose onClick={() => dismiss(toast.id)} />
                    </Toast>
                ))}
                <ToastViewport />
            </RadixToastProvider>
        </ToastContext.Provider>
    );
}
